// This optional code is used to register a service worker.
// register() is not called by default.

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
    onSuccess?: (registration: ServiceWorkerRegistration) => void;
    onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

const SERVICE_WORKER_UPDATE_CHECK_INTERVAL = 5 * 60 * 1000;

const notifyIfWaiting = (registration: ServiceWorkerRegistration, config?: Config) => {
    if (registration.waiting && navigator.serviceWorker.controller) {
        config?.onUpdate?.(registration);
        return true;
    }

    return false;
};

const startUpdateChecks = (registration: ServiceWorkerRegistration, config?: Config) => {
    const checkForUpdate = () => {
        registration.update()
            .then(() => {
                notifyIfWaiting(registration, config);
            })
            .catch((error) => {
                console.warn('Service worker update check failed:', error);
            });
    };

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            checkForUpdate();
        }
    });

    window.addEventListener('focus', checkForUpdate);
    window.addEventListener('pageshow', checkForUpdate);
    window.addEventListener('online', checkForUpdate);
    window.setInterval(checkForUpdate, SERVICE_WORKER_UPDATE_CHECK_INTERVAL);

    checkForUpdate();
};

export function applyServiceWorkerUpdate(registration: ServiceWorkerRegistration) {
    const waitingWorker = registration.waiting;

    let refreshing = false;

    const reloadOnce = () => {
        if (refreshing) {
            return;
        }

        refreshing = true;
        window.location.reload();
    };

    if (!waitingWorker) {
        return;
    }

    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);

    waitingWorker.postMessage({type: 'SKIP_WAITING'});
}

export function register(config?: Config) {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);

        if (publicUrl.origin !== window.location.origin) {
            return;
        }

        window.addEventListener('load', () => {
            const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

            if (isLocalhost) {
                checkValidServiceWorker(swUrl, config);

                navigator.serviceWorker.ready.then(() => {
                    console.log(
                        'This web app is being served cache-first by a service worker.'
                    );
                });
            } else {
                registerValidSW(swUrl, config);
            }
        });
    }
}

function registerValidSW(swUrl: string, config?: Config) {
    navigator.serviceWorker
        .register(swUrl, { updateViaCache: 'none' })
        .then((registration) => {
            registration.onupdatefound = () => {
                const installingWorker = registration.installing;

                if (installingWorker == null) {
                    return;
                }

                installingWorker.onstatechange = () => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            console.log('New content is available.');

                            config?.onUpdate?.(registration);
                        } else {
                            console.log('Content is cached for offline use.');

                            config?.onSuccess?.(registration);
                        }
                    }
                };
            };

            /**
             * Quan trọng cho iPhone PWA:
             * Nếu service worker mới đã nằm sẵn ở trạng thái waiting,
             * lần mở app sau vẫn hiện toast "Có phiên bản mới".
             */
            notifyIfWaiting(registration, config);

            /**
             * Chủ động check bản service-worker.js mới khi mở app, quay lại tab,
             * có mạng lại và định kỳ để PWA đang mở lâu vẫn hiện nút tải lại.
             */
            startUpdateChecks(registration, config);
        })
        .catch((error) => {
            console.error('Error during service worker registration:', error);
        });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
    fetch(swUrl, {
        headers: {'Service-Worker': 'script'},
    })
        .then((response) => {
            const contentType = response.headers.get('content-type');

            if (
                response.status === 404 ||
                (contentType != null && contentType.indexOf('javascript') === -1)
            ) {
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => {
                        window.location.reload();
                    });
                });
            } else {
                registerValidSW(swUrl, config);
            }
        })
        .catch(() => {
            console.log('No internet connection found. App is running in offline mode.');
        });
}

export function unregister() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => {
                registration.unregister();
            })
            .catch((error) => {
                console.error(error.message);
            });
    }
}